import './styles.scss'

import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { DeleteBorderlessIcon, HeartIcon } from '@/components/Icons'
import { useAppDispatch } from '@/store/hook'
import { sendProductDetail, showAddProductToWishlistModal } from '@/store/slices/wishlistSlice'
import PermissionWrapper from '@/HOCs/permissionWrapper'
import { MODULE_WISHLIST_RIGHTS } from '@/libs/constants/modulerights'

import ComparisonBlock from '../ComparisonBlock'
import ProductComparisonCard from '../ProductComparisonCard'

interface ProductComparisonTableProps {
  memoListProduct: string
  handleRemoveProduct: (...args: unknown[]) => unknown
}

const ProductComparisonTable = ({
  memoListProduct,
  handleRemoveProduct,
  ...props
}: ProductComparisonTableProps) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const listProduct = useMemo(
    () => JSON.parse(memoListProduct),
    [memoListProduct],
  )

  const listProductIds = Object.keys(listProduct)

  return (
    <table
      className={`product-comparison-table ${`number-items-${listProductIds.length}`}`}
    >
      <thead>
        <tr>
          <th>
            <div style={{ width: 400 }} />
          </th>
          {listProductIds.map((productId) => {
            const productData = listProduct[productId]

            if (!productData) {
              return (
                <th key={`header-${productId}`}>
                  <div style={{ width: 300 }} />
                </th>
              )
            }

            return (
              <th key={`header-${productId}`}>
                <div style={{ width: 300 }}>
                  <div className="product-comparison-card__action-buttons">
                    <PermissionWrapper
                      permission={MODULE_WISHLIST_RIGHTS.ADD_PRODUCT_CHANGE_WISHLIST}
                    >
                      <button
                        className="button-as-link"
                        type="button"
                        onClick={() => {
                          dispatch(
                            sendProductDetail({
                              materialName: productData.materialName,
                              materialNumber: productData.materialNumber,
                              img: productData.image,
                            }),
                          )
                          dispatch(showAddProductToWishlistModal())
                        }}
                      >
                        <HeartIcon className="add-to-wishlist__button" />
                      </button>
                    </PermissionWrapper>
                    <button
                      className="button-as-link"
                      type="button"
                      onClick={() => handleRemoveProduct(productId)}
                    >
                      <DeleteBorderlessIcon height="24" width="24" />
                    </button>
                  </div>
                  <ProductComparisonCard {...productData} />
                </div>
              </th>
            )
          })}
        </tr>
      </thead>
      <tbody>
        <ComparisonBlock
          listProduct={listProduct}
          type="technical"
          {...props}
        />
      </tbody>
      <tbody>
        <ComparisonBlock
          listProduct={listProduct}
          type="equipment"
          {...props}
        />
      </tbody>
      <tbody>
        <tr>
          <td className="product-comparison-table__header" colSpan="4">
            <h4 className="font-weight-bold">
              {t('productComparison.description')}
            </h4>
          </td>
        </tr>
        <tr>
          <td />
          {listProductIds.map((productId) => {
            const productData = listProduct[productId]

            return (
              <td key={`${productId}-description`}>
                <div className="product-comparison-table__product-description">
                  {productData?.longDescription?.split('<br/>').join('')}
                </div>
              </td>
            )
          })}
        </tr>
      </tbody>
    </table>
  )
}

export default ProductComparisonTable
